import { Photo, PhotoArchive, PICTURE_SIZE_720P } from '../lib/photoarchive';
import { IS_HIDDEN, GALLERY_LOADER, GALLERY_PICTURE_PREFIX, KEYBOARD, ANIMATION } from '../constants';
import replaceState from '../helpers/replace-state';
import translate from '../helpers/translate';
import Toast from '../helpers/toast-notification';
import getURLHash from '../helpers/get-url-hash';
import { addScrollbarDifference, removeScrollbarDifference } from '../helpers/scrollbar-difference';
import { picTuple } from '../typings';
import { blockContextMenu } from './prevent-context-menu';
import 'swiped-events';
import sendEvent from '../helpers/tracking/analytics-wrapper';

interface Pictures {
    invisibleImage: HTMLPictureElement | null;
    visibleImage: HTMLPictureElement | null;
}

enum CLASS {
    HIDDEN = 'hlp--hidden',
    SLIDE_IMAGE = 'slide-image',
    SLIDESHOW_BUTTON_HIDE = 'gallery__slideshow-btn--hide',
}

const photoGallery: PhotoArchive = getAvailablePictures();
const SLIDESHOW_SECONDS = 5;
let slideShowTimer: ReturnType<typeof setInterval>;
let lockButton = false;
let isSlideShowMode = false;

export default function gallery(pictureId: number) {
    const photo = photoGallery.current();

    photoGallery.setPictureId(pictureId);

    attachGalleryEvents();
    replaceState({ pictureID: photo.id }, '', `#${GALLERY_PICTURE_PREFIX}-${photo.id}`);
    buildGallery();
}

function buildOverlay(): void {
    const galleryID = photoGallery.current().getAlbumId();
    const overlay: HTMLDivElement = document.createElement('div');

    overlay.id = `album-${galleryID}`;
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-label', 'Gallery');
    overlay.classList.add('modal__overlay', 'modal__overlay--transparent', 'modal__overlay--gallery');

    addScrollbarDifference();

    document.body.appendChild(overlay);
}

function buildGallery(): void {
    buildOverlay();

    const galleryID = photoGallery.current().getAlbumId();
    const overlay: HTMLDivElement | null = document.querySelector(`.modal__overlay#album-${galleryID}`);

    if (overlay) {
        const modal: HTMLDivElement = document.createElement('div');
        const close: HTMLAnchorElement = document.createElement('a');

        close.href = '#';
        close.setAttribute('aria-label', 'Close');
        close.classList.add('modal__close', 'js--modal__close');

        modal.appendChild(close);
        modal.classList.add('modal', 'modal--visible', 'modal--gallery');

        if (photoArchive?.isMobile) {
            modal.classList.add('mobile');
        }

        overlay.appendChild(modal);

        sendEvent({
            category: 'Gallery',
            action: 'Profile',
            label: 'Open - Click',
        });

        blockContextMenu(modal);
        setPictures(overlay, modal);
        const navigation = setNavigation(modal);
        const videoButton = setVideoButton(modal);
        setSlideShowButton(modal, videoButton, navigation);
    }
}

function setPictures(overlay: HTMLDivElement, modal: HTMLDivElement): void {
    const photo: Photo = photoGallery.current();
    const visibleImage: HTMLPictureElement = initPicture(photo, overlay);
    visibleImage.id = 'js-picture-first';
    visibleImage.classList.add(ANIMATION.RIGHT_TO_CENTER);

    const invisibleImage: HTMLPictureElement = initPicture(photo, overlay);
    invisibleImage.id = 'js-picture-second';

    const galleryPicture: HTMLDivElement = document.createElement('div');
    galleryPicture.classList.add('gallery__picture');
    galleryPicture.appendChild(visibleImage);
    galleryPicture.appendChild(invisibleImage);
    modal.appendChild(galleryPicture);

    visibleImage.addEventListener('animationend', () => {
        lockButton = false;
        visibleImage.classList.remove(GALLERY_LOADER);
        invisibleImage.classList.remove(GALLERY_LOADER);
    });
    visibleImage.addEventListener('animationstart', () => {
        lockButton = true;
        visibleImage.classList.add(GALLERY_LOADER);
        invisibleImage.classList.add(GALLERY_LOADER);
    });

    sendEvent({
        category: 'Gallery',
        action: `Album - ${photo.getAlbumId()}`,
        label: `Picture - ${photo.id}`,
    });
}

function initPicture(picture: Photo, overlay: HTMLDivElement): HTMLPictureElement {
    const imageWrp: HTMLPictureElement = document.createElement('picture');
    imageWrp.setAttribute('data-id', `${picture.id}`);
    imageWrp.classList.add(GALLERY_LOADER);

    const picturePath = photoGallery.getPath();

    for (const key in picturePath) {
        if (key) {
            const path = picturePath[key];

            if (key === PICTURE_SIZE_720P) {
                const image: HTMLImageElement = document.createElement('img');
                image.loading = 'eager';
                image.id = `image-${key}`;
                image.decoding = 'sync';
                image.addEventListener('load', (e: Event) => {
                    overlay.classList.remove('modal__overlay--transparent');
                    overlay.classList.add('modal__overlay--visible');
                    image.classList.remove(CLASS.HIDDEN);
                });
                image.addEventListener('error', (e: Event) => {
                    overlay.remove();

                    removeScrollbarDifference();

                    Toast.show({
                        message: translate(901),
                        type: 'warning',
                    });

                    sendEvent({
                        category: 'Gallery',
                        action: `Album - ${picture.getAlbumId()}`,
                        label: `Picture error - ${picture.id}`,
                    });
                });

                image.src = `${path}${picture.getImagePath()}`;
                imageWrp.appendChild(image);
            } else {
                const imageMedia: HTMLSourceElement = document.createElement('source');
                imageMedia.type = 'image/jpeg';
                imageMedia.media = `(min-width: ${key}x)`;
                imageMedia.srcset = `${path}${picture.getImagePath()}`;
                imageMedia.id = `image-${key}`;

                imageWrp.prepend(imageMedia);
            }
        }
    }

    return imageWrp;
}

function updatePicture(galleryPicture: HTMLPictureElement, picture: Photo): void {
    galleryPicture.setAttribute('data-id', `${picture.id}`);
    const picturePath = photoGallery.getPath();

    for (const key in picturePath) {
        if (key) {
            const path = picturePath[key];
            const elm = galleryPicture.querySelector<HTMLImageElement | HTMLSourceElement>(`#image-${key}`);
            const imageFullPath = `${path}${picture.getImagePath()}`;

            if (elm instanceof HTMLImageElement) {
                elm.classList.add(CLASS.HIDDEN);
                elm.src = imageFullPath;
            }

            if (elm instanceof HTMLSourceElement) {
                elm.srcset = imageFullPath;
            }
        }
    }

    sendEvent({
        category: 'Gallery',
        action: `Album - ${picture.getAlbumId()}`,
        label: `Picture - ${picture.id}`,
    });

    replaceState({ pictureID: picture.id }, '', `#${GALLERY_PICTURE_PREFIX}-${picture.id}`);
}

function maybeHideButton(btn: HTMLDivElement) {
    if (photoGallery.isFirstPhoto() && photoGallery.isLastPhoto()) {
        btn.classList.add(IS_HIDDEN);
    } else {
        btn.classList.remove(IS_HIDDEN);
    }
}

function setNavigation(modal: HTMLDivElement): HTMLDivElement {
    const navigation: HTMLDivElement = document.createElement('div');
    navigation.classList.add('gallery__navigation');

    const btnPrevious: HTMLDivElement = document.createElement('div');
    btnPrevious.classList.add('gallery__navigation-previous');
    btnPrevious.title = translate(902);

    const btnNext: HTMLDivElement = document.createElement('div');
    btnNext.classList.add('gallery__navigation-next');
    btnNext.title = translate(903);

    btnPrevious.addEventListener('click', () => {
        if (!lockButton) {
            const photo: Photo = photoGallery.prev();
            prevPicture(photo);
            maybeHideButton(btnNext);
            maybeHideButton(btnPrevious);
        }
    });

    btnNext.addEventListener('click', () => {
        if (!lockButton) {
            const photo: Photo = photoGallery.next();
            nextPicture(photo);
            maybeHideButton(btnNext);
            maybeHideButton(btnPrevious);
        }
    });

    navigation.appendChild(btnPrevious);
    navigation.appendChild(btnNext);

    maybeHideButton(btnPrevious);
    maybeHideButton(btnNext);

    modal.appendChild(navigation);

    return navigation;
}

function getAvailablePictures(): PhotoArchive {
    const pictures: Photo[] = [];

    photoArchive.gallery.forEach((data: picTuple) => {
        pictures.push(new Photo(...data));
    });

    return new PhotoArchive(photoArchive.pathHD, pictures);
}

function setVideoButton(modal: HTMLDivElement): HTMLAnchorElement {
    const videoButtonContainer: HTMLDivElement = document.createElement('div');
    videoButtonContainer.classList.add('hlp--ac', 'gallery__action');

    const videoButton: HTMLAnchorElement = document.createElement('a');
    videoButton.innerHTML = `<i class="icon ${photoArchive.videoButtonIcon}" aria-hidden="true"></i> <span>${photoArchive.videoButtonText}</span>`;

    if (photoArchive?.isMobile) {
        videoButton.classList.add('btn', 'btn--round');
    } else {
        videoButton.classList.add('btn', 'btn--primary');
    }

    if (!photoArchive.isLoggedOn) {
        videoButton.href = '/logon.html';
        videoButton.setAttribute('data-modal', '');
        videoButton.setAttribute('data-to', photoArchive.goToURL);
    } else {
        videoButton.href = photoArchive.confirmChargesURL;
        videoButton.setAttribute('data-modal', '');
        videoButton.setAttribute('data-modal-options', '{"size": "auto", "header": false, "class": "confirm-charges"}');
    }

    if (!photoArchive.isModelLive) {
        videoButton.classList.add('btn--secondary', 'btn--disabled', 'is-offline');
    }

    videoButton.addEventListener('click', () => {
        closeModal();
    });

    videoButtonContainer.appendChild(videoButton);
    modal.appendChild(videoButtonContainer);
    return videoButton;
}

function setSlideShowButton(modal: HTMLElement, videoButton: HTMLAnchorElement, navigation: HTMLDivElement): void {
    const buttonsContainer: HTMLDivElement = document.createElement('div');
    buttonsContainer.classList.add('gallery__slideshow-action');

    const showButton: HTMLAnchorElement = document.createElement('a');
    showButton.text = photoArchive.slideShowButtonText;
    showButton.classList.add('btn', 'btn--outline', 'gallery__slideshow-btn');
    showButton.addEventListener('click', _handleSlideShow);

    const stopButton: HTMLAnchorElement = document.createElement('a');
    stopButton.text = photoArchive.slideStopButtonText;
    stopButton.classList.add('btn', 'btn--outline', 'gallery__slideshow-btn', CLASS.SLIDESHOW_BUTTON_HIDE);
    stopButton.addEventListener('click', _handleSlideStop);
    stopButton.addEventListener('transitionend', _handleTransitionEnd);

    let isButtonsEnabled = true;

    function _handleTransitionEnd() {
        isButtonsEnabled = true;
    }

    const modalClose: HTMLAnchorElement | null = document.querySelector('.js--modal__close');
    const firstPicture: HTMLImageElement | null = document.querySelector('#js-picture-first > img');
    const secondPicture: HTMLImageElement | null = document.querySelector('#js-picture-second > img');

    const line: HTMLDivElement = document.createElement('div');
    line.classList.add('js--gallery__slideshow-line', 'gallery__slideshow-line');
    modal.prepend(line);

    function _handleSlideShow(e: MouseEvent) {
        e.preventDefault();

        if (isButtonsEnabled) {
            isButtonsEnabled = false;
            showButton.classList.add(CLASS.SLIDESHOW_BUTTON_HIDE);
            stopButton.classList.remove(CLASS.SLIDESHOW_BUTTON_HIDE);
            videoButton.classList.add('gallery__slideshow-btn--video');

            navigation.classList.add(CLASS.HIDDEN);
            modalClose?.classList.add(CLASS.HIDDEN);

            modal.classList.add('gallery__slideshow-bg');
            firstPicture?.classList.add(CLASS.SLIDE_IMAGE);
            secondPicture?.classList.add(CLASS.SLIDE_IMAGE);
            isSlideShowMode = true;
            startSlideShow();
        }
    }

    function _handleSlideStop(e: MouseEvent) {
        e.preventDefault();

        if (isButtonsEnabled) {
            stopSlideShow();
            navigation.classList.remove(CLASS.HIDDEN);
            modalClose?.classList.remove(CLASS.HIDDEN);
            firstPicture?.classList.remove(CLASS.SLIDE_IMAGE);
            secondPicture?.classList.remove(CLASS.SLIDE_IMAGE);
            modal.classList.remove('gallery__slideshow-bg');

            isButtonsEnabled = false;
            stopButton.classList.add(CLASS.SLIDESHOW_BUTTON_HIDE);
            showButton.classList.remove(CLASS.SLIDESHOW_BUTTON_HIDE);

            videoButton.classList.remove('gallery__slideshow-btn--video');
        }
    }

    buttonsContainer.appendChild(showButton);
    buttonsContainer.appendChild(stopButton);

    modal.appendChild(buttonsContainer);
}

function startSlideShow() {
    if (slideShowTimer) {
        clearInterval(slideShowTimer);
    }

    if (isSlideShowMode) {
        const line = document.querySelector('.js--gallery__slideshow-line');
        line?.setAttribute('style', `transition: width ${SLIDESHOW_SECONDS}s linear; width: 100vw`);
        slideShowTimer = setInterval(function () {
            nextImage();
            line?.setAttribute('style', `transition: width ${SLIDESHOW_SECONDS}s linear; width: 100vw`);
        }, SLIDESHOW_SECONDS * 1000);
    }
}

function stopSlideShow() {
    resetAnimatedLine();

    if (slideShowTimer) {
        clearInterval(slideShowTimer);
    }

    isSlideShowMode = false;
}

function resetAnimatedLine() {
    if (isSlideShowMode) {
        const line = document.querySelector('.js--gallery__slideshow-line');
        line?.removeAttribute('style');
    }
}

function isElementHidden(el: HTMLElement) {
    const style = window.getComputedStyle(el);
    return style.opacity === '0';
}

function keyboardEvents(e: KeyboardEvent): void {
    const key: string = e.key;

    if (key === KEYBOARD.ARROW_LEFT) {
        showNextImage(true);
    }

    if (key === KEYBOARD.ARROW_RIGHT) {
        showNextImage();
    }

    if (e.key === KEYBOARD.ESC) {
        stopSlideShow();
    }
}

function showNextImage(isPrev: boolean = false): void {
    if (isPrev) {
        previousImage();
    } else {
        nextImage();
    }
    startSlideShow();
}

function nextImage(): void {
    const btnNext: HTMLDivElement | null = document.querySelector('.gallery__navigation-next');

    resetAnimatedLine();
    if (btnNext && !isElementHidden(btnNext)) {
        btnNext.click();
    }
}

function previousImage(): void {
    const btnPrevious: HTMLDivElement | null = document.querySelector('.gallery__navigation-previous');

    resetAnimatedLine();
    if (btnPrevious && !isElementHidden(btnPrevious)) {
        btnPrevious.click();
    }
}

function popstateActions(): void {
    const hash = getURLHash(document.location);

    if (!hash) {
        closeModal();
    }
}

function closeModal(): void {
    const modalClose: HTMLAnchorElement | null = document.querySelector('.js--modal__close');

    stopSlideShow();

    if (modalClose) {
        modalClose.click();
    }
}

function getPictures(): Pictures {
    const firstPicture: HTMLPictureElement | null = document.querySelector('#js-picture-first');
    const secondPicture: HTMLPictureElement | null = document.querySelector('#js-picture-second');

    const invisibleImage = secondPicture && isElementHidden(secondPicture) ? secondPicture : firstPicture;
    const visibleImage = secondPicture && !isElementHidden(secondPicture) ? secondPicture : firstPicture;

    return { invisibleImage, visibleImage };
}

function nextPicture(photo: Photo) {
    const { invisibleImage, visibleImage }: Pictures = getPictures();

    if (invisibleImage && visibleImage) {
        updatePicture(invisibleImage, photo);
        updatePictureClasses(visibleImage, ANIMATION.CENTER_TO_LEFT);
        updatePictureClasses(invisibleImage, ANIMATION.RIGHT_TO_CENTER);
    }
}

function prevPicture(photo: Photo) {
    const { invisibleImage, visibleImage }: Pictures = getPictures();

    if (invisibleImage && visibleImage) {
        updatePicture(invisibleImage, photo);
        updatePictureClasses(invisibleImage, ANIMATION.LEFT_TO_CENTER);
        updatePictureClasses(visibleImage, ANIMATION.CENTER_TO_RIGHT);
    }
}

function updatePictureClasses(picture: HTMLPictureElement, currentClass: string) {
    const animationClasses: string[] = [ANIMATION.CENTER_TO_LEFT, ANIMATION.CENTER_TO_RIGHT, ANIMATION.RIGHT_TO_CENTER, ANIMATION.LEFT_TO_CENTER].filter((item: string) => {
        return item !== currentClass;
    });
    picture.classList.add(currentClass);
    picture.classList.remove(...animationClasses);
}

function swipeEventLeft(): void {
    showNextImage();
}
function swipeEventRight(): void {
    showNextImage(true);
}

function attachGalleryEvents(): void {
    window.removeEventListener('popstate', popstateActions);
    window.addEventListener('popstate', popstateActions);

    window.removeEventListener('keydown', keyboardEvents);
    window.addEventListener('keydown', keyboardEvents);

    document.removeEventListener('swiped-left', swipeEventLeft);
    document.addEventListener('swiped-left', swipeEventLeft);

    document.removeEventListener('swiped-right', swipeEventRight);
    document.addEventListener('swiped-right', swipeEventRight);

    document.removeEventListener('swiped-up', swipeEventLeft);
    document.addEventListener('swiped-up', swipeEventLeft);

    document.removeEventListener('swiped-down', swipeEventRight);
    document.addEventListener('swiped-down', swipeEventRight);
}
